// Centralized SEO configuration and Schema.org generators
import { LOCATIONS, SERVICES, BLOG, BRAND, TESTIMONIALS, IMAGES } from "./site";

export const SITE_URL = "https://smilesbydoctork.com";

export const DEFAULT_OG_IMAGE = IMAGES.smileCenter;

// --- Schema.org generators ---

const phoneToTel = (p) => "+1" + p.replace(/\D/g, "");

const addressParts = (addr) => {
  // "100 Craig Road, Suite 102, Manalapan, NJ 07726"
  const parts = addr.split(",").map((s) => s.trim());
  return {
    streetAddress: parts.slice(0, parts.length - 2).join(", "),
    addressLocality: parts[parts.length - 2] || "",
    addressRegion: (parts[parts.length - 1] || "").split(" ")[0] || "",
    postalCode: (parts[parts.length - 1] || "").split(" ")[1] || "",
    addressCountry: "US",
  };
};

const hoursToSchema = (hours) => {
  const map = { "Mon": "Mo", "Tue": "Tu", "Wed": "We", "Thu": "Th", "Fri": "Fr", "Sat": "Sa", "Sun": "Su" };
  const result = [];
  hours.forEach((h) => {
    if (/closed/i.test(h.time) || /appointment/i.test(h.time)) return;
    const m = h.time.match(/(\d{1,2}:\d{2})\s*(AM|PM)\s*–\s*(\d{1,2}:\d{2})\s*(AM|PM)/i);
    if (!m) return;
    const to24 = (t, ap) => {
      let [hr, mn] = t.split(":").map(Number);
      if (/pm/i.test(ap) && hr !== 12) hr += 12;
      if (/am/i.test(ap) && hr === 12) hr = 0;
      return `${String(hr).padStart(2, "0")}:${String(mn).padStart(2, "0")}`;
    };
    const opens = to24(m[1], m[2]);
    const closes = to24(m[3], m[4]);
    const days = h.day.split("–").map((s) => s.trim());
    let dayList = [];
    if (days.length === 2) {
      const order = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
      const start = order.indexOf(days[0]);
      const end = order.indexOf(days[1]);
      if (start >= 0 && end >= 0) dayList = order.slice(start, end + 1);
    } else if (days.length === 1) {
      dayList = [days[0]];
    }
    dayList.forEach((d) => result.push({ "@type": "OpeningHoursSpecification", dayOfWeek: map[d] || d, opens, closes }));
  });
  return result;
};

const geoFor = (slug) => {
  // Approximate (city-level) coordinates
  if (slug === "galleria") return { latitude: 40.2879, longitude: -74.3343 };
  if (slug === "essential") return { latitude: 40.7831, longitude: -73.6498 };
  return null;
};

export const dentistSchema = (loc) => {
  const a = addressParts(loc.address);
  const g = geoFor(loc.slug);
  return {
    "@context": "https://schema.org",
    "@type": "Dentist",
    "@id": `${SITE_URL}/locations/${loc.slug}#dentist`,
    name: `${BRAND.name} — ${loc.name}`,
    alternateName: loc.name,
    description: loc.description,
    image: loc.image,
    url: `${SITE_URL}/locations/${loc.slug}`,
    telephone: phoneToTel(loc.phone),
    email: loc.email,
    priceRange: "$$$",
    address: { "@type": "PostalAddress", ...a },
    geo: g ? { "@type": "GeoCoordinates", ...g } : undefined,
    openingHoursSpecification: hoursToSchema(loc.hours),
    areaServed: a.addressRegion === "NJ"
      ? ["Manalapan", "Marlboro", "Holmdel", "Freehold", "Monmouth County"]
      : ["Roslyn", "Roslyn Heights", "Manhasset", "Great Neck", "Nassau County"],
    medicalSpecialty: ["CosmeticDentistry", "GeneralDentistry"],
  };
};

export const organizationSchema = () => ({
  "@context": "https://schema.org",
  "@type": "Organization",
  "@id": `${SITE_URL}#organization`,
  name: BRAND.name,
  alternateName: "Smiles by Dr K",
  url: SITE_URL,
  logo: `${SITE_URL}/logo.png`,
  slogan: BRAND.tagline,
  founder: { "@type": "Person", name: BRAND.doctor },
  sameAs: ["https://instagram.com/smilesbydrk"],
  department: LOCATIONS.map((l) => ({ "@id": `${SITE_URL}/locations/${l.slug}#dentist` })),
});

export const websiteSchema = () => ({
  "@context": "https://schema.org",
  "@type": "WebSite",
  "@id": `${SITE_URL}#website`,
  url: SITE_URL,
  name: BRAND.name,
  description: BRAND.tagline,
  publisher: { "@id": `${SITE_URL}#organization` },
});

export const personSchema = () => ({
  "@context": "https://schema.org",
  "@type": "Person",
  "@id": `${SITE_URL}/about#drk`,
  name: BRAND.doctor,
  jobTitle: "Doctor of Dental Surgery",
  worksFor: { "@id": `${SITE_URL}#organization` },
  image: IMAGES.drKPortrait,
  url: `${SITE_URL}/about`,
  description: "Cosmetic and restorative dentist, founder of Smiles by Dr. K. Trained in cosmetic, restorative, and holistic dentistry.",
  alumniOf: ["Kois Center", "Spear Education", "AACD"],
});

export const serviceSchema = (svc) => ({
  "@context": "https://schema.org",
  "@type": "MedicalProcedure",
  name: svc.name,
  procedureType: svc.category,
  description: svc.summary,
  image: svc.image,
  url: `${SITE_URL}/services/${svc.slug}`,
  performer: { "@id": `${SITE_URL}#organization` },
});

export const breadcrumbSchema = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((it, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: it.name,
    item: `${SITE_URL}${it.path}`,
  })),
});

export const itemListSchema = (name, items) => ({
  "@context": "https://schema.org",
  "@type": "ItemList",
  name,
  itemListElement: items.map((it, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: it.name,
    url: it.url,
  })),
});

export const blogSchema = () => ({
  "@context": "https://schema.org",
  "@type": "Blog",
  name: "The Smile Journal",
  url: `${SITE_URL}/journal`,
  publisher: { "@id": `${SITE_URL}#organization` },
  blogPost: BLOG.map((p) => ({
    "@type": "BlogPosting",
    headline: p.title,
    image: p.image,
    datePublished: p.date,
    url: `${SITE_URL}/journal/${p.slug}`,
  })),
});

export const blogPostSchema = (post) => ({
  "@context": "https://schema.org",
  "@type": "BlogPosting",
  headline: post.title,
  description: post.excerpt,
  image: post.image,
  datePublished: post.date,
  author: { "@type": "Person", name: BRAND.doctor },
  publisher: { "@id": `${SITE_URL}#organization` },
  mainEntityOfPage: `${SITE_URL}/journal/${post.slug}`,
  articleSection: post.category,
});

export const aggregateRatingSchema = () => ({
  "@context": "https://schema.org",
  "@type": "Dentist",
  "@id": `${SITE_URL}#organization`,
  name: BRAND.name,
  aggregateRating: {
    "@type": "AggregateRating",
    ratingValue: "4.9",
    reviewCount: "400",
    bestRating: "5",
    worstRating: "1",
  },
  review: TESTIMONIALS.slice(0, 5).map((t) => ({
    "@type": "Review",
    reviewRating: { "@type": "Rating", ratingValue: String(t.rating), bestRating: "5" },
    author: { "@type": "Person", name: t.name },
    reviewBody: t.text,
  })),
});

export const contactPageSchema = () => ({
  "@context": "https://schema.org",
  "@type": "ContactPage",
  name: `Contact ${BRAND.name}`,
  url: `${SITE_URL}/contact`,
  about: { "@id": `${SITE_URL}#organization` },
});

export const faqSchema = (faqs) => {
  if (!faqs?.length) return null;
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };
};

// --- Per-page SEO descriptors ---

export const SEO = {
  home: {
    title: `${BRAND.name} | Dentistry & Aesthetics — ${BRAND.tagline}`,
    description: `${BRAND.doctor} — cosmetic and restorative dentistry with two New York / New Jersey studios. Veneers, smile makeovers, Invisalign, implants, and holistic dental care.`,
    keywords: "cosmetic dentist, porcelain veneers, smile makeover, Invisalign, dental implants, Manalapan NJ dentist, Roslyn Heights NY dentist",
  },
  about: {
    title: `About Dr. K — ${BRAND.name}`,
    description: `Meet ${BRAND.doctor}, founder of ${BRAND.name}. Cosmetic and restorative dentistry, practiced as a craft. Trained at Kois Center, Spear Education, and the AACD.`,
  },
  services: {
    title: `Services — Cosmetic & Restorative Dentistry | ${BRAND.name}`,
    description: "Porcelain veneers, smile makeovers, Invisalign, dental implants, teeth whitening, and full-mouth reconstruction — every service delivered as fine craft.",
  },
  technology: {
    title: `Technology — ${BRAND.name}`,
    description: "CBCT 3D imaging, iTero & PrimeScan scanners, Biolase Waterlase, Digital Smile Design and more. Precision tools, in service of human craft.",
  },
  reviews: {
    title: `Patient Reviews — ${BRAND.name}`,
    description: `Read what patients of ${BRAND.doctor} have to say. 4.9 average across 400+ Google reviews.`,
  },
  gallery: {
    title: `Smile Gallery — Before & After Cases | ${BRAND.name}`,
    description: "Real patients. Real results. Browse before/after cases of veneers, whitening, Invisalign, implants and full smile makeovers.",
  },
  journal: {
    title: `The Smile Journal — ${BRAND.name}`,
    description: "Notes from the studio on cosmetic dentistry, the craft of veneers, whitening, holistic dentistry, and Digital Smile Design.",
  },
  contact: {
    title: `Contact — ${BRAND.name}`,
    description: `Book a visit at Galleria Dental (Manalapan, NJ) or Essential Dental (Roslyn Heights, NY). Text our team or send us a message.`,
  },
  notFound: {
    title: `Page Not Found — ${BRAND.name}`,
    description: "We couldn't find this page.",
    noindex: true,
  },
};

export const buildServicePageSEO = (svc) => ({
  title: `${svc.name} — ${BRAND.name}`,
  description: svc.summary,
});

export const buildBlogPostSEO = (post) => ({
  title: `${post.title} — The Smile Journal | ${BRAND.name}`,
  description: post.excerpt,
});

export const buildLocationSEO = (loc) => ({
  title: `${loc.name} — ${loc.city} | ${BRAND.name}`,
  description: `${loc.name} in ${loc.city}. ${loc.focus}. Cosmetic and restorative dentistry by ${BRAND.doctor}.`,
});

// Convenience: build pre-baked Service-level schemas list
export { SERVICES, LOCATIONS, BLOG };
